import { useRef, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Textarea } from '@artstorefronts/ui';
import { Loader2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { UPDATE_CHAT_EVALUATION_MUTATION } from '../../graphql/queries';

interface EvaluationFormData {
  rating: 'good' | 'bad' | null;
  notes: string;
}

interface ChatEvaluationProps {
  chatId: string;
  initialGood?: boolean | null;
  initialNotes?: string | null;
  onSave?: () => void;
}

function ChatEvaluation({ chatId, initialGood, initialNotes, onSave }: ChatEvaluationProps) {
  const [updateChatEvaluation, { loading: saving }] = useMutation(UPDATE_CHAT_EVALUATION_MUTATION);
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');
  const notesRef = useRef<HTMLTextAreaElement>(null);

  const { control, handleSubmit, watch, reset, setValue } = useForm<EvaluationFormData>({
    defaultValues: {
      rating: initialGood === true ? 'good' : initialGood === false ? 'bad' : null,
      notes: initialNotes || '',
    },
  });

  const rating = watch('rating');

  const onSubmit = useCallback(
    async (data: EvaluationFormData) => {
      try {
        const good = data.rating === 'good' ? true : data.rating === 'bad' ? false : null;

        await updateChatEvaluation({
          variables: {
            input: {
              id: chatId,
              good,
              notes: data.notes || null,
            },
          },
          optimisticResponse: {
            updateChatEvaluation: {
              __typename: 'Chat',
              id: chatId,
              good,
              notes: data.notes || null,
              updatedAt: new Date().toISOString(),
            },
          },
        });

        toast.success(t('evaluation.saved'));
        onSave?.();
      } catch (error) {
        console.error('Error saving evaluation:', error);
        toast.error(t('evaluation.save-failed'));
      }
    },
    [chatId, updateChatEvaluation, onSave]
  );

  // Keyboard shortcuts: Cmd/Ctrl + Enter to submit, G/B to rate and focus notes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(onSubmit)();
        return;
      }

      // Don't trigger G/B shortcuts when typing in input fields
      const isTyping =
        (e.target as HTMLElement)?.tagName === 'INPUT' ||
        (e.target as HTMLElement)?.tagName === 'TEXTAREA' ||
        (e.target as HTMLElement)?.isContentEditable;

      if (!isTyping && (e.key === 'g' || e.key === 'G')) {
        e.preventDefault();
        setValue('rating', 'good');
        notesRef.current?.focus();
        return;
      }

      if (!isTyping && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setValue('rating', 'bad');
        notesRef.current?.focus();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSubmit, onSubmit, setValue]);

  return (
    <div className="bg-white border rounded-md shadow-md">
      <div className="p-6">
        <h3 className="text-lg font-medium mb-4">{t('evaluation.title')}</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Controller
              name="rating"
              control={control}
              render={({ field }) => (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => field.onChange('good')}
                    className={`px-6 py-3 border rounded-md transition-colors ${
                      rating === 'good'
                        ? 'bg-emerald-500 border-emerald-600 text-white'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <ThumbsUp className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => field.onChange('bad')}
                    className={`px-6 py-3 border rounded-md transition-colors ${
                      rating === 'bad'
                        ? 'bg-red-500 border-red-600 text-white'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <ThumbsDown className="w-5 h-5" />
                  </button>
                </div>
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('evaluation.notes-label')}</label>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  ref={notesRef}
                  rows={4}
                  className="w-full"
                  placeholder={t('evaluation.notes-placeholder')}
                />
              )}
            />
          </div>

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">G</kbd>
                <span>{t('evaluation.good')}</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">B</kbd>
                <span>{t('evaluation.bad')}</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono flex items-center">
                  <span>⌘</span>
                  <span className="ml-0.5">↵</span>
                </kbd>
                <span>{tc('save')}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => reset()} disabled={saving}>
                {t('evaluation.reset')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {tc('save')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChatEvaluation;
